import { ReactNode } from "react";
import { Card, CardContent, Box, Tooltip, Typography } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { TrendValue } from "../../models/dto/cat15/cat15.types";
import InfoPopper from "../popper/InfoPopper";

interface Props {
  title: string;
  /** already-formatted value, e.g. "12,450" */
  value: string;
  /** unit shown after the value, e.g. "tCO₂e" */
  unit?: string;
  /** control under the title, e.g. the intensity basis picker */
  titleAction?: ReactNode;
  /** title-row height; the dashboard raises it on every stat card so one card's control cannot break the row of values */
  titleMinHeight?: number;
  /** space between the title, value and badge rows (Figma borrower-detail Stat_Card uses 4px) */
  rowGap?: number;
  /** grey the value, e.g. when few loans stand behind it */
  muted?: boolean;
  trend?: TrendValue;
  /** caption under the trend, e.g. "vs 2024" */
  trendCaption?: string;
  /** when set the pill shows "—" instead of a change, with this reason on hover */
  trendSuppressedReason?: string;
  /** short text beside the pill, e.g. how many loans the value covers */
  trendNote?: ReactNode;
  /** optional info tooltip shown as an ⓘ icon next to the title */
  info?: string;
  /** colored glyph shown in the tinted circle left of the title */
  icon?: ReactNode;
  /** glyph color inside the tinted circle */
  iconColor?: string;
  /** tinted circle background behind the icon */
  iconBg?: string;
  /**
   * Draw a 1px card border. Default true — needed on white backgrounds like
   * the borrower Summary tab. The dashboard stat cards sit on a grey page and
   * are borderless in Figma, so they pass bordered={false}.
   */
  bordered?: boolean;
}

/**
 * Year-on-year change pill: arrow + signed change + caption on a tinted ground
 * (green good, red bad, grey flat). A suppressed change shows "—" with the
 * reason on hover.
 */
export const TrendPill = ({
  trend,
  caption,
  suppressedReason,
}: {
  trend: TrendValue;
  caption?: string;
  suppressedReason?: string;
}) => {
  // A zero delta has no direction — render it neutral (no arrow, gray) instead
  // of a misleading up-arrow with a good/bad color. A suppressed change is neutral too.
  const isFlat = trend.delta === 0 || !!suppressedReason;
  const isUp = trend.delta > 0;
  // A change is "good" when its direction matches higherIsBetter.
  const isGood = isUp === trend.higherIsBetter;
  const trendColor = isFlat
    ? "var(--gray9)"
    : isGood
    ? "var(--status-approved-complete-active-text)"
    : "var(--red)";
  // tinted highlight pill behind the delta (gray when flat, green good, red bad)
  const trendBg = isFlat
    ? "var(--gray6)"
    : isGood
    ? "var(--status-15)"
    : "var(--red-12)";

  const pill = (
    <Box
      display="inline-flex"
      alignItems="center"
      gap="2px"
      px="10px"
      py="4px"
      borderRadius="8px"
      bgcolor={trendBg}
      width="fit-content"
      flexShrink={0}
    >
      {isFlat ? null : isUp ? (
        <ArrowDropUpIcon fontSize="small" sx={{ color: trendColor }} />
      ) : (
        <ArrowDropDownIcon fontSize="small" sx={{ color: trendColor }} />
      )}
      <Typography
        fontSize="12px"
        fontWeight={600}
        lineHeight="17.16px"
        color={trendColor}
      >
        {suppressedReason ? "—" : `${Math.abs(trend.delta)}${trend.deltaUnit}`}
      </Typography>
      {caption && (
        <Typography
          fontSize="12px"
          lineHeight="17.16px"
          color={trendColor}
          ml="2px"
        >
          {caption}
        </Typography>
      )}
    </Box>
  );

  return suppressedReason ? (
    <Tooltip arrow title={suppressedReason}>
      {pill}
    </Tooltip>
  ) : (
    pill
  );
};

/** KPI stat card — Figma Card/Stat_Card (node 6229:14897). */
const EmissionsKpiCard = ({
  title,
  value,
  unit,
  titleAction,
  titleMinHeight = 40,
  rowGap = 8,
  muted = false,
  trend,
  trendCaption,
  trendSuppressedReason,
  trendNote,
  info,
  icon,
  iconColor = "var(--green)",
  iconBg = "var(--tint-green)",
  bordered = true,
}: Props) => {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "8px",
        border: bordered ? "1px solid var(--gray5)" : "none",
        boxSizing: "border-box",
      }}
    >
      <CardContent
        sx={{
          // Figma strokes sit inside the box, so a bordered card's padding
          // gives up that pixel: 1 + 19 + 52 + 19 + 1 = the 92px Stat_Card.
          p: bordered ? "19px" : "20px",
          "&:last-child": { pb: bordered ? "19px" : "20px" },
          height: "100%",
          boxSizing: "border-box",
          // The 148px Figma card height comes from title + value + KPI badge.
          // Without a trend badge (e.g. the borrower DetailSummary cards) don't
          // force 148px or the value floats to the bottom of an empty box.
          minHeight: trend ? 148 : undefined,
          display: "flex",
          flexDirection: "column",
          gap: `${rowGap}px`,
          justifyContent: trend ? "space-between" : "flex-start",
        }}
      >
        {/* Title row: tinted icon circle · title · info */}
        <Box display="flex" alignItems="center" gap="12px" minHeight={titleMinHeight}>
          {icon && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: iconColor,
                backgroundColor: iconBg,
                "& svg": { fontSize: 18 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
              <Typography
                fontSize="14px"
                fontWeight={500}
                lineHeight="24px"
                letterSpacing={0}
                color="var(--dark)"
              >
                {title}
              </Typography>
              {info && <InfoPopper text={info} />}
            </Box>
            {titleAction}
          </Box>
        </Box>

        {/* Value row */}
        <Box display="flex" alignItems="center" gap="8px" height={24}>
          <Typography
            fontSize="20px"
            fontWeight={700}
            lineHeight="24px"
            color={muted ? "var(--gray1)" : "var(--dark)"}
          >
            {value}
          </Typography>
          {unit && (
            <Typography
              fontSize="12px"
              lineHeight="24px"
              color="var(--gray1)"
              minWidth={0}
              noWrap
            >
              {unit}
            </Typography>
          )}
        </Box>

        {/* KPI badge + note */}
        {trend && (
          <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
            <TrendPill trend={trend} caption={trendCaption} suppressedReason={trendSuppressedReason} />
            {trendNote && (
              <Typography
                fontSize="12px"
                lineHeight="17.16px"
                color="var(--gray1)"
                minWidth={0}
                noWrap
              >
                {trendNote}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EmissionsKpiCard;
